import React, { useCallback, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { withRouter } from 'react-router-dom'

import { withApollo } from '@apollo/client/react/hoc'
import { FontContextProvider } from '@toasttab/sites-components'

import { isClient } from 'src/lib/js/detector'
import useQueryString from 'src/lib/js/hooks/useQueryString'
import useTracker from 'src/lib/js/hooks/useTracker'
import {
  getReferrerChannel,
  getReferrerProperties,
  getOwgFeedProperty
} from 'src/lib/js/trackerUtilities'

import { getFontFaceAndFamily } from 'public/components/default_template/meta/StyleMeta'

import { server } from 'config'

import { StaticSiteContextProvider } from './StaticSiteContext'
import { useCookieConsentContext } from 'src/public/js/cookie_consent/CookieConsentContext'

import { t } from 'src/translations'

const App = ({ staticContext, location, children }) => {
  const { ethycaEnabled } = useCookieConsentContext()

  // Determine bot status: server-side uses staticContext, client-side reads the serialized window flag
  const isBotSession = isClient()
    ? Boolean(window.__IS_BOT__)
    : Boolean(staticContext?.isBot)
  const isEditorSession = Boolean(staticContext?.isEditor)
  const blacklisted = !isClient() || isBotSession || isEditorSession

  // Enable tracking for analytics
  const tracker = useTracker({
    blacklisted,
    debug: false
  })
  // If ethyca is not enabled, set consent to true. Otherwise, consent is handled by TrackerConsentHandler.tsx
  if (!ethycaEnabled) {
    tracker.setConsent(true)
    tracker.setAdsConsent(true)
  }

  const { rwg_token: rwgToken } = useQueryString()

  /**
   * Capture or force 'reset' order-with-google user property for tracking to avoid stale owg attribution
   * across event data. Particularly important for authenticated user sessions with ID resolution, or Amplitude sessions
   * shared across restaurant websites.
   */
  useEffect(() => {
    if (typeof sessionStorage !== 'undefined') {
      const currentRwgToken = sessionStorage.getItem('rwg_token')

      if (rwgToken) {
        sessionStorage.setItem('rwg_token', rwgToken)
        tracker.register({ rwgToken })
      } else if (!currentRwgToken) {
        tracker.unregister(['rwgToken'])
      }
    }
  }, [rwgToken, tracker])

  /**
   * Force 'reset' referrer user properties for tracking to avoid stale user referrer attribution properties
   * across event data. Without, sessions started from a different referrer will be attributed to the previous referrer.
   * Additionally, for authenticated users starting new sessions, the referrer will be the previous session's referrer.
   */
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const [referrer, referringDomain] = getReferrerProperties()
      if (referrer && referringDomain) {
        tracker.register({ referrer, referring_domain: referringDomain })
      } else {
        // removing the property preserves the default (None) categorization in Amplitude when no referrer is present
        tracker.unregister(['referrer', 'referring_domain'])
      }
    }
  }, [tracker])

  /**
   * Capture the referrer channel and OWG feed property on initial page load and persist them as
   * session event properties. registerSessionEventProperties handles sessionStorage persistence,
   * so the values survive reloads without needing to be re-detected.
   */
  useEffect(() => {
    if (typeof window === 'undefined') return
    const channel = getReferrerChannel(window.location.href, document.referrer)
    const owgFeed = getOwgFeedProperty(window.location.href)
    if (channel || owgFeed) {
      tracker.registerSessionEventProperties({
        ...(channel && { 'toast-referrer-channel': channel }),
        ...(owgFeed && { 'owg-feed': owgFeed })
      })
    }
  }, [tracker])

  // Use react-helmet for head tag updates
  const defaultCanonical = `${server.protocol}://${server.host}${
    location.pathname != '/' ? location.pathname : ''
  }`
  const helmet = (
    <Helmet defaultTitle={t('app.default-title')}>
      <link rel='canonical' href={defaultCanonical} />
    </Helmet>
  )

  const skipToMainContent = useCallback((e) => {
    if (
      e.type === 'keydown' &&
      e.code !== 'Escape' &&
      e.code !== 'Space' &&
      e.code !== 'Enter'
    ) {
      return
    }
    e.preventDefault()
    const mainEl = document.getElementById('main')
    if (mainEl) {
      if (!mainEl.hasAttribute('tabindex')) {
        mainEl.setAttribute('tabindex', '-1')
      }
      mainEl.focus()
      mainEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [])

  // Used to stop the "Skip to Main Content" from rendering in iframes
  const [isInIframe, setIsInIframe] = useState(false)
  useEffect(() => {
    setIsInIframe(window.self !== window.top)
  }, [])

  return (
    <StaticSiteContextProvider value={staticContext}>
      <FontContextProvider
        renderGoogleFonts={(fonts) => (
          <Helmet>
            {fonts.map((font) => (
              <link
                key={font}
                href={`https://fonts.googleapis.com/css?family=${font}:wght@400;600;700&display=swap`}
                rel='stylesheet'
              />
            ))}
          </Helmet>
        )}
        renderFonts={(styles) => (
          <Helmet>
            <style>{styles}</style>
          </Helmet>
        )}
        toFontFace={(font) => {
          return getFontFaceAndFamily({ name: font.name, url: font.src })
            .fontFace
        }}
      >
        {!isInIframe && (
          <a
            href='#main'
            className='skip-link'
            onClick={(e) => skipToMainContent(e)}
            onKeyDown={(e) => skipToMainContent(e)}
          >
            {t('app.skip-to-main-content')}
          </a>
        )}
        {helmet}
        <div className='tsw-app-wrapper' id='tsw-app-wrapper'>
          {children}
        </div>
      </FontContextProvider>
    </StaticSiteContextProvider>
  )
}

export default withRouter(withApollo(App))
